<script setup>
import OrderProgress from '@/pages/order/components/OrderProgress.vue'
import { useSelfOrderStore } from '@/stores/selfOrder'
import { formatRupiah } from '@/utils/formatRupiah'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const route = useRoute()
const store = useSelfOrderStore()

const orders = ref([])
const isLoading = ref(true)
const errorMessage = ref('')

const statusMeta = {
  pending: { label: 'Menunggu Konfirmasi Kasir', color: 'warning', icon: 'ri-time-line' },
  paid: { label: 'Sudah Dibayar', color: 'info', icon: 'ri-bank-card-line' },
  preparing: { label: 'Sedang Dibuat', color: 'secondary', icon: 'ri-fire-line' },
  ready: { label: 'Siap Diambil', color: 'primary', icon: 'ri-hand-heart-line' },
  completed: { label: 'Selesai', color: 'success', icon: 'ri-checkbox-circle-line' },
  voided: { label: 'Dibatalkan', color: 'error', icon: 'ri-close-circle-line' },
}

const timelineOrder = ['pending', 'paid', 'preparing', 'ready', 'completed']

const metaFor = status => statusMeta[status] || { label: status, color: 'secondary', icon: 'ri-information-line' }

const timelineIndex = status => {
  const index = timelineOrder.indexOf(status)

  return index === -1 ? 0 : index
}

const loadOrders = async () => {
  errorMessage.value = ''
  try {
    orders.value = await store.fetchOrders()
  }
  catch (err) {
    errorMessage.value = err?.data?.message || err?.message || 'Gagal memuat status pesanan.'
  }
  finally {
    isLoading.value = false
  }
}

let pollTimer = null

onMounted(() => {
  loadOrders()
  // TODO Modul 18: ganti ke Pusher/Echo (event OrderPaid/StationTicketUpdated)
  // begitu broadcast realtime tersedia. Untuk Modul 15, polling tiap 8 detik.
  pollTimer = setInterval(loadOrders, 8000)
})

onBeforeUnmount(() => clearInterval(pollTimer))
</script>

<template>
  <div class="status-screen pb-8">
    <VAppBar flat color="surface" density="comfortable" class="status-appbar">
      <template #prepend>
        <VBtn
          icon
          variant="text"
          :to="{ name: 'order-session-token-menu', params: { sessionToken: route.params.sessionToken } }"
        >
          <VIcon icon="ri-arrow-left-line" />
        </VBtn>
      </template>
      <VAppBarTitle>
        Status Pesanan
      </VAppBarTitle>
      <template #append>
        <span class="d-flex align-center gap-1 text-caption text-medium-emphasis">
          <span class="poll-dot" />
          live
        </span>
      </template>
    </VAppBar>

    <div class="px-4">
      <OrderProgress :step="5" />
    </div>

    <VContainer>
      <template v-if="isLoading">
        <VSkeletonLoader v-for="n in 3" :key="n" type="list-item-two-line" rounded="lg" class="mb-3" />
      </template>

      <VAlert v-else-if="errorMessage" type="error" variant="tonal" rounded="lg">
        {{ errorMessage }}
      </VAlert>

      <div v-else-if="!orders.length" class="status-empty text-center py-12">
        <VIcon icon="ri-file-list-3-line" size="56" color="disabled" class="mb-3" />
        <p class="text-body-1 font-weight-medium mb-1">
          Belum ada pesanan
        </p>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Pesanan Anda pada sesi ini akan muncul di sini.
        </p>
      </div>

      <VExpansionPanels v-else variant="accordion" class="status-panels">
        <VExpansionPanel v-for="order in orders" :key="order.id" class="status-panel mb-3" rounded="lg">
          <VExpansionPanelTitle>
            <div class="d-flex align-center justify-space-between flex-grow-1 pe-2">
              <div>
                <p class="text-body-1 font-weight-bold mb-0">
                  {{ order.order_number }}
                </p>
                <p class="text-caption text-medium-emphasis mb-0">
                  {{ order.items_count }} item · {{ formatRupiah(order.total) }}
                </p>
              </div>
              <VChip size="small" :color="metaFor(order.status).color" variant="tonal">
                <VIcon :icon="metaFor(order.status).icon" size="14" start />
                {{ metaFor(order.status).label }}
              </VChip>
            </div>
          </VExpansionPanelTitle>

          <VExpansionPanelText>
            <div v-if="order.status !== 'voided'" class="order-timeline mb-2">
              <div
                v-for="(key, index) in timelineOrder"
                :key="key"
                class="order-timeline-step"
                :class="{
                  'is-done': index < timelineIndex(order.status),
                  'is-active': index === timelineIndex(order.status),
                }"
              >
                <span class="order-timeline-node">
                  <VIcon :icon="statusMeta[key].icon" size="12" />
                </span>
                <span class="order-timeline-label">{{ statusMeta[key].label.split(' ')[0] }}</span>
              </div>
            </div>

            <VList v-if="order.items?.length" density="compact" class="pa-0">
              <VListItem v-for="(item, idx) in order.items" :key="idx" class="px-0">
                <VListItemTitle class="text-body-2 d-flex justify-space-between">
                  <span>
                    {{ item.quantity }}x {{ item.product_name || item.name }}
                    <span v-if="item.variant_name && item.variant_name.toLowerCase() !== 'default'">
                      ({{ item.variant_name }})
                    </span>
                  </span>
                  <span v-if="item.subtotal != null" class="font-weight-medium">
                    {{ formatRupiah(item.subtotal) }}
                  </span>
                </VListItemTitle>
              </VListItem>
            </VList>

            <!-- Invoice — muncul begitu order sudah tercatat pembayarannya -->
            <template v-if="order.payments?.length">
              <VDivider class="my-3" />
              <div class="d-flex justify-space-between text-body-2 mb-1">
                <span>Subtotal</span>
                <span>{{ formatRupiah(order.subtotal) }}</span>
              </div>
              <div
                v-for="line in order.discount_breakdown ?? []"
                :key="line.label"
                class="d-flex justify-space-between text-body-2 mb-1"
              >
                <span>{{ line.label }}</span>
                <span>-{{ formatRupiah(line.amount) }}</span>
              </div>
              <div v-if="order.tax_amount > 0" class="d-flex justify-space-between text-body-2 mb-1">
                <span>Pajak</span>
                <span>{{ formatRupiah(order.tax_amount) }}</span>
              </div>
              <div class="d-flex justify-space-between text-body-1 font-weight-bold mb-2">
                <span>Total</span>
                <span>{{ formatRupiah(order.total) }}</span>
              </div>
              <VDivider class="my-2" />
              <div
                v-for="(payment, idx) in order.payments"
                :key="idx"
                class="d-flex justify-space-between text-body-2 text-medium-emphasis"
              >
                <span class="text-capitalize">Dibayar via {{ payment.method }}</span>
                <span>{{ formatRupiah(payment.amount) }}</span>
              </div>
            </template>
          </VExpansionPanelText>
        </VExpansionPanel>
      </VExpansionPanels>

      <VBtn
        block
        size="large"
        rounded="lg"
        color="primary"
        variant="tonal"
        class="mt-2"
        :to="{ name: 'order-session-token-menu', params: { sessionToken: route.params.sessionToken } }"
      >
        <VIcon icon="ri-add-line" start />
        Pesan Lagi
      </VBtn>
    </VContainer>
  </div>
</template>

<style scoped>
.status-screen {
  background: rgb(var(--v-theme-background));
  min-block-size: 100vh;
}

.status-appbar {
  border-block-end: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}

.poll-dot {
  inline-size: 6px;
  block-size: 6px;
  border-radius: 50%;
  background: rgb(var(--v-theme-success));
  animation: poll-pulse 1.6s ease-in-out infinite;
}

@keyframes poll-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}

.status-panel {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06) !important;
  border-radius: 16px !important;
  overflow: hidden;
}

.order-timeline {
  display: flex;
  align-items: flex-start;
  padding-block: 4px 8px;
}

.order-timeline-step {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.order-timeline-step:not(:last-child)::after {
  content: '';
  position: absolute;
  inset-block-start: 10px;
  inset-inline-start: calc(50% + 14px);
  inline-size: calc(100% - 28px);
  block-size: 2px;
  background: rgba(var(--v-theme-on-surface), 0.12);
}

.order-timeline-step.is-done:not(:last-child)::after {
  background: rgb(var(--v-theme-success));
}

.order-timeline-node {
  display: flex;
  align-items: center;
  justify-content: center;
  inline-size: 22px;
  block-size: 22px;
  border-radius: 50%;
  background: rgba(var(--v-theme-on-surface), 0.08);
  color: rgba(var(--v-theme-on-surface), 0.4);
  z-index: 1;
}

.order-timeline-step.is-done .order-timeline-node {
  background: rgb(var(--v-theme-success));
  color: white;
}

.order-timeline-step.is-active .order-timeline-node {
  background: rgb(var(--v-theme-primary));
  color: white;
  box-shadow: 0 0 0 4px rgba(var(--v-theme-primary), 0.16);
}

.order-timeline-label {
  margin-block-start: 4px;
  font-size: 10px;
  color: rgba(var(--v-theme-on-surface), 0.5);
  text-align: center;
}

@media (prefers-reduced-motion: reduce) {
  .poll-dot { animation: none; }
}
</style>
